export type MasterImage = { blob: Blob; width: number; height: number };

// Self-contained worker: no network library import, source bytes remain untouched.
const workerSource = `
self.onmessage = async (event) => {
  try {
    const source = event.data;
    const image = await createImageBitmap(source, {imageOrientation: "from-image"});
    try {
      const type = ["image/png", "image/webp", "image/avif"].includes(source.type) ? "image/png" : "image/jpeg";
      let scale = Math.min(1, 3200 / Math.max(image.width, image.height));
      const minimumEdge = Math.min(1600, Math.max(image.width, image.height));
      while (true) {
        const width = Math.max(1, Math.round(image.width * scale));
        const height = Math.max(1, Math.round(image.height * scale));
        // A detailed photo saved as PNG or WebP rarely fits as PNG: try JPEG (on white) before shrinking.
        for (const output of type === "image/png" ? ["image/png", "image/jpeg"] : ["image/jpeg"]) {
          const canvas = new OffscreenCanvas(width, height);
          const context = canvas.getContext("2d");
          if (!context) throw new Error("Image processing unavailable");
          if (output === "image/jpeg") { context.fillStyle = "#fff"; context.fillRect(0, 0, width, height); }
          context.drawImage(image, 0, 0, width, height);
          for (const quality of [0.88, 0.85, 0.82]) {
            const blob = await canvas.convertToBlob({type: output, quality});
            if (blob.size <= 2500000) {
              self.postMessage({blob, width, height}); return;
            }
            if (output === "image/png") break;
          }
        }
        if (Math.max(width, height) <= minimumEdge)
          throw new Error("This image needs server normalization; the original is unchanged");
        scale = Math.max(minimumEdge / Math.max(image.width, image.height), scale * 0.85);
      }
    } finally { image.close(); }
  } catch (error) { self.postMessage({error: error.message || "Image processing failed"}); }
};`;

export async function compressImage(source: Blob): Promise<MasterImage> {
  // Keep the File byte-for-byte for checksum and archival; only normalize the operational master.
  if (["image/heic", "image/heif"].includes(source.type.toLowerCase()) ||
      (typeof File !== "undefined" && source instanceof File && /\.hei[cf]$/i.test(source.name))) {
    try {
      const { heicTo } = await import("heic-to/csp");
      const jpeg = await heicTo({ blob: source, type: "image/jpeg", quality: 0.88 });
      if (jpeg.type !== "image/jpeg" || !jpeg.size) throw new Error("Invalid converted image");
      return compressImage(jpeg);
    } catch {
      throw new Error("HEIC conversion failed; the original photograph is unchanged");
    }
  }
  // Some systems hand an AVIF over with no type; the browser decodes it once it is labelled.
  if (!source.type && typeof File !== "undefined" && source instanceof File && /\.avif$/i.test(source.name))
    return compressImage(new Blob([source], { type: "image/avif" }));
  if (!["image/jpeg", "image/png", "image/webp", "image/avif", "image/bmp"].includes(source.type))
    throw new Error("This image format needs server normalization; please select JPEG or PNG");
  if (typeof Worker === "undefined" || typeof OffscreenCanvas === "undefined")
    return compressOnCanvas(source);
  const url = URL.createObjectURL(new Blob([workerSource], { type: "text/javascript" }));
  return new Promise<MasterImage>((resolve, reject) => {
    let worker: Worker;
    const cleanup = () => { worker?.terminate(); URL.revokeObjectURL(url); clearTimeout(timeout); };
    const timeout = setTimeout(() => { cleanup(); reject(new Error("Image processing timed out")); }, 60000);
    try {
      worker = new Worker(url);
      worker.onerror = () => { cleanup(); reject(new Error("Image processing unavailable")); };
      worker.onmessage = event => {
        cleanup();
        if (event.data.error) reject(new Error(event.data.error));
        else resolve(event.data as MasterImage);
      };
      worker.postMessage(source);
    } catch {
      cleanup(); reject(new Error("Image processing unavailable"));
    }
  }).catch(() => compressOnCanvas(source));
}


// Compatibility path for browsers without worker canvas. Yield between encoding attempts.
async function compressOnCanvas(source: Blob): Promise<MasterImage> {
  const url = URL.createObjectURL(source);
  const image = new Image();
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("This browser cannot decode this photograph"));
      image.src = url;
    });
    const longest = Math.max(image.naturalWidth, image.naturalHeight);
    if (!longest) throw new Error("Invalid image dimensions");
    const minimum = Math.min(1600, longest);
    let scale = Math.min(1, 3200 / longest);
    const type = ["image/png", "image/webp", "image/avif"].includes(source.type) ? "image/png" : "image/jpeg";
    while (true) {
      const width = Math.max(1, Math.round(image.naturalWidth * scale));
      const height = Math.max(1, Math.round(image.naturalHeight * scale));
      // Same as the worker: PNG first for PNG/WebP sources, then JPEG on white before shrinking.
      for (const output of type === "image/png" ? ["image/png", "image/jpeg"] : ["image/jpeg"]) {
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        const context = canvas.getContext("2d");
        if (!context) throw new Error("Image processing unavailable");
        if (output === "image/jpeg") { context.fillStyle = "#fff"; context.fillRect(0, 0, width, height); }
        context.drawImage(image, 0, 0, width, height);
        for (const quality of [0.88, 0.85, 0.82]) {
          const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob(
            value => value ? resolve(value) : reject(new Error("Image encoding failed")), output, quality));
          if (blob.size <= 2500000) return { blob, width, height };
          if (output === "image/png") break;
        }
      }
      if (Math.max(width, height) <= minimum)
        throw new Error("This photograph cannot meet the master size limit; select a smaller copy and retain the original");
      scale = Math.max(minimum / longest, scale * 0.85);
      await new Promise(resolve => window.setTimeout(resolve, 0));
    }
  } finally { URL.revokeObjectURL(url); }
}
